import { Dispatch, ReactElement, SetStateAction, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/_atoms/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/_atoms/dropdown-menu";
import { IconDots } from "@/components/_icons/IconDots";
import { IconTrash } from "@/components/_icons/IconTrash";
import { DeleteConfirmation } from "@/components/_molecules/delete-confirmation";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { useCanModuleAction } from "@/domain/_shared/module-access/hooks/use-can-module-action";
import { useEntityPermissions } from "@/domain/_shared/permission/hooks/use-entity-permissions";
import { useRemoveMunicipalPropertyContent } from "@/domain/municipal-property/hooks/api/use-remove-municipal-property-content";
import { MUNICIPAL_PROPERTY_BASE_PATH } from "@/domain/municipal-property/routes/municipal-property-base-path";
import { useCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";
import { makeWorkspaceUrl } from "@/domain/workspace/lib/make-workspace-url";

export function MunicipalPropertyRegisterActions({ registerId }: { registerId: string }): ReactElement | null {
  const { t }: ReturnType<typeof useDomainTranslation> = useDomainTranslation("municipal-property");
  const { currentWorkspace }: ReturnType<typeof useCurrentWorkspaceContext> = useCurrentWorkspaceContext();
  const navigate: ReturnType<typeof useNavigate> = useNavigate();
  const [isDeleteOpen, setIsDeleteOpen]: [boolean, Dispatch<SetStateAction<boolean>>] = useState(false);
  const { canDelete }: ReturnType<typeof useEntityPermissions> = useEntityPermissions({ entityId: registerId, resource: "municipalPropertyRegister" });
  const canCreate: boolean = useCanModuleAction({ module: "MUNICIPAL_PROPERTY", action: "create" });
  const { removeRegister }: ReturnType<typeof useRemoveMunicipalPropertyContent> = useRemoveMunicipalPropertyContent({ canRestore: canDelete && canCreate });
  const canDeleteInModule: boolean = useCanModuleAction({ module: "MUNICIPAL_PROPERTY", action: "delete" });

  async function handleRemove(): Promise<void> {
    try {
      await removeRegister.mutateAsync(registerId);
      setIsDeleteOpen(false);
      void navigate(makeWorkspaceUrl({ workspace: currentWorkspace, path: `${MUNICIPAL_PROPERTY_BASE_PATH}/registers` }));
    } catch {
      toast.error(t("register.removeError"));
    }
  }

  if (!canDelete || !canDeleteInModule) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            aria-label={t("register.openActions")}
            className="text-muted-foreground"
          >
            <IconDots className="h-4 w-4 shrink-0" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="rounded-md">
          <DropdownMenuItem disabled={removeRegister.isPending} onClick={() => setIsDeleteOpen(true)}>
            <IconTrash className="mr-2 h-3 w-3 text-muted-foreground" />
            {t("register.delete")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <DeleteConfirmation
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        title={t("register.removeConfirmTitle")}
        description={t("register.removeConfirmDescription")}
        onConfirm={() => void handleRemove()}
        onCancel={() => setIsDeleteOpen(false)}
      />
    </>
  );
}
